import { createFileRoute } from "@tanstack/react-router";
import { createElement, useEffect } from "react";

import { defineOpenGraphProperty } from "./-common";

import { HomePage } from "~/pages/home";
import { DEFAULT_LANGUAGE_CODE, LanguageCode } from "~/shared/i18n";
import { EMPTY, SLASH } from "~/shared/str";

function Component() {
  const { locale } = Route.useParams();

  useEffect(() => {
    const tags = Object.values(LanguageCode)
      .filter((value) => value !== locale)

      .map((content) => {
        const tag = document.createElement("meta");
        for (const [key, value] of Object.entries(
          defineOpenGraphProperty({
            property: "locale:alternate",
            content,
          }),
        )) {
          tag.setAttribute(key, value);
        }
        return tag;
      });

    for (const tag of tags) {
      document.head.appendChild(tag);
    }

    return () => {
      for (const tag of tags) {
        tag.remove();
      }
    };
  }, [locale]);

  return createElement(HomePage);
}

export const Route = createFileRoute("/{-$locale}/")({
  loader: ({ context: { config }, params: { locale } }) => ({
    config,
    locale,
  }),
  head: ({ loaderData: { config, locale } = {} }) => {
    const { host } = config?.app ?? {};
    const canonicalUrl = [host, locale].join(SLASH);
    return {
      links: [
        {
          rel: "canonical",
          href: canonicalUrl,
        },
        ...[
          ...Object.values(LanguageCode).map((language) => ({
            language,
            hrefLang: language,
          })),
          {
            language: DEFAULT_LANGUAGE_CODE,
            hrefLang: "x-default",
          },
        ].map(({ hrefLang, language }) => ({
          rel: "alternate",
          hrefLang,
          href: [host, language].join(SLASH),
        })),
      ],
      meta: [
        ...[
          {
            property: "url",
            content: canonicalUrl,
          },
          {
            property: "locale",
            content: locale ?? EMPTY,
          },
        ].map(defineOpenGraphProperty),
      ],
    };
  },
  component: Component,
});
